@if (edit) {
    <clp-toolbar [title]="('digitalProduct.update.title' | translate)"></clp-toolbar>
}
@else {
    <clp-toolbar [title]="('digitalProduct.create.title' | translate)"></clp-toolbar>
}

<div class="px-4 py-3">
  <div class="mx-auto w-full max-w-4xl">
    <form class="space-y-4" (ngSubmit)="changeDigitalProduct(digitalProductForm.value)" [formGroup]="digitalProductForm" novalidate>
      <input type="hidden" formControlName="id">

      <section class="clp-card">
        <div class="clp-card-body space-y-4">
          <h3 class="text-base font-semibold text-slate-100">{{ 'digitalProduct.generalData' | translate }}</h3>

          <div class="grid grid-cols-1 gap-4 md:grid-cols-12">
            <div class="md:col-span-6">
              <label for="name" class="clp-label">{{ 'digitalProduct.name' | translate }}</label>
              <input id="name" type="text" class="clp-input" formControlName="name" required>
              @if (digitalProductForm.controls['name'].invalid && digitalProductForm.controls['name'].touched) {
                <div class="clp-help">{{ 'digitalProduct.create.invalidName' | translate }}</div>
              }
            </div>

            <div class="md:col-span-3">
              <label for="currency" class="clp-label">{{ 'digitalProduct.currency' | translate }}</label>
              <select id="currency" class="clp-select" formControlName="currency" required>
                <option value="USD">{{ 'general.currency.usd' | translate }}</option>
                <option value="EUR">{{ 'general.currency.eur' | translate }}</option>
                <option value="BRL">{{ 'general.currency.brl' | translate }}</option>
              </select>
              @if (digitalProductForm.controls['currency'].invalid && digitalProductForm.controls['currency'].touched) {
                <div class="clp-help">{{ 'digitalProduct.create.invalidCurrency' | translate }}</div>
              }
            </div>

            <div class="md:col-span-3">
              <label for="visibility" class="clp-label">{{ 'digitalProduct.visibilityLabel' | translate }}</label>
              <select id="visibility" class="clp-select" formControlName="visibility" required>
                @for (visibility of visibilities; track visibility) {
                  <option [value]="visibility.value">{{ visibility.label | translate }}</option>
                }
              </select>
              @if (digitalProductForm.controls['visibility'].invalid && digitalProductForm.controls['visibility'].touched) {
                <div class="clp-help">{{ 'digitalProduct.create.invalidVisibility' | translate }}</div>
              }
            </div>
          </div>

          <div>
            <label for="description" class="clp-label">{{ 'digitalProduct.description' | translate }}</label>
            <textarea id="description" class="clp-textarea" formControlName="description" rows="4"></textarea>
            @if (digitalProductForm.controls['description'].invalid && digitalProductForm.controls['description'].touched) {
              <div class="clp-help">{{ 'digitalProduct.create.invalidDescription' | translate }}</div>
            }
          </div>

          <div class="max-w-md">
            <label for="coverUrl" class="clp-label">{{ 'digitalProduct.coverUrl' | translate }}</label>
            <clp-image-upload
              (imageFile)="coverChanged($event)"
              (deleteImageFile)="deleteCover($event)"
              [imageDataUrl]="imageUrl">
            </clp-image-upload>
          </div>
        </div>
      </section>

      <section class="clp-card">
        <div class="clp-card-body space-y-4">
          <h3 class="text-base font-semibold text-slate-100">{{ 'digitalProduct.billingData' | translate }}</h3>

          <div class="grid grid-cols-1 gap-4 md:grid-cols-12">
            <div class="md:col-span-4">
              <label for="billingType" class="clp-label">{{ 'digitalProduct.billingTypeLabel' | translate }}</label>
              <select id="billingType" class="clp-select" formControlName="billingType" required>
                @for (billingType of billingTypes; track billingType) {
                  <option [value]="billingType.value">{{ billingType.label | translate }}</option>
                }
              </select>
              @if (digitalProductForm.controls['billingType'].invalid && digitalProductForm.controls['billingType'].touched) {
                <div class="clp-help">{{ 'digitalProduct.create.invalidBillingType' | translate }}</div>
              }
            </div>

            @if (isRecurring()) {
              <div class="md:col-span-4">
                <label for="recurringFee" class="clp-label">{{ 'digitalProduct.recurringFee' | translate }}</label>
                <input id="recurringFee" type="number" class="clp-input" formControlName="recurringFee" min="0.01" step="0.01">
                @if (digitalProductForm.controls['recurringFee'].invalid && digitalProductForm.controls['recurringFee'].touched) {
                  <div class="clp-help">{{ 'digitalProduct.create.invalidRecurringFee' | translate }}</div>
                }
              </div>

              <div class="md:col-span-4">
                <label for="recurringInterval" class="clp-label">{{ 'digitalProduct.recurringIntervalLabel' | translate }}</label>
                <select id="recurringInterval" class="clp-select" formControlName="recurringInterval">
                  @for (recurringInterval of recurringIntervals; track recurringInterval) {
                    @if (recurringInterval.value !== 0) {
                      <option [value]="recurringInterval.value">{{ recurringInterval.label | translate }}</option>
                    }
                  }
                </select>
                @if (digitalProductForm.controls['recurringInterval'].invalid && digitalProductForm.controls['recurringInterval'].touched) {
                  <div class="clp-help">{{ 'digitalProduct.create.invalidRecurringInterval' | translate }}</div>
                }
              </div>

              <div class="md:col-span-6">
                <label for="recurringIntervalCount" class="clp-label">{{ 'digitalProduct.recurringIntervalCount' | translate }}</label>
                <input id="recurringIntervalCount" type="number" class="clp-input" formControlName="recurringIntervalCount" min="1" max="365">
                <p class="mt-1 text-xs text-slate-400">{{ 'digitalProduct.recurringIntervalCountHelp' | translate }}</p>
                @if (digitalProductForm.controls['recurringIntervalCount'].invalid && digitalProductForm.controls['recurringIntervalCount'].touched) {
                  <div class="clp-help">{{ 'digitalProduct.create.invalidRecurringIntervalCount' | translate }}</div>
                }
              </div>

              <div class="md:col-span-6">
                <label for="trialDays" class="clp-label">{{ 'digitalProduct.trialDays' | translate }}</label>
                <input id="trialDays" type="number" class="clp-input" formControlName="trialDays" min="0" max="365">
                @if (digitalProductForm.controls['trialDays'].invalid && digitalProductForm.controls['trialDays'].touched) {
                  <div class="clp-help">{{ 'digitalProduct.create.invalidTrialDays' | translate }}</div>
                }
              </div>
            } @else {
              <div class="md:col-span-4">
                <label for="oneTimeFee" class="clp-label">{{ 'digitalProduct.oneTimeFee' | translate }}</label>
                <input id="oneTimeFee" type="number" class="clp-input" formControlName="oneTimeFee" min="0.01" step="0.01">
                @if (digitalProductForm.controls['oneTimeFee'].invalid && digitalProductForm.controls['oneTimeFee'].touched) {
                  <div class="clp-help">{{ 'digitalProduct.create.invalidOneTimeFee' | translate }}</div>
                }
              </div>
            }
          </div>
        </div>
      </section>

      <div class="flex justify-end gap-2">
        <a class="clp-btn clp-btn-secondary" [routerLink]="['/listDigitalProducts']">{{ 'general.cancel' | translate }}</a>
        <button class="clp-btn clp-btn-warning" type="submit">{{ 'general.save' | translate }}</button>
      </div>
    </form>
  </div>
</div>
